// [PURPOSE]: Auto-expire non-concentration spell effects from effectRegistry
//   when rest events or calendar advances cross their duration boundary.
// [WHY]: spellCastingService.castSpell registers spell.spellEffects under
//   sourceId 'spell:cast:<spellName>:<timestamp>'. Concentration spells get
//   cleaned up by concentrationSystem.breakConcentration (commit Phase 4b
//   CW-parity-C). Non-concentration spells (Mage Armor 8h, Stoneskin 1h,
//   Aid 8h, Heroes' Feast 24h) currently leak forever — this service closes
//   that lifecycle gap.
// [INTEGRATION]: Single-init listener subscribed in App.jsx (init once, like
//   concentrationSystem). Listens to:
//     - REST_SHORT_STARTED  → retracts spells with duration ≤ 1 hour
//     - REST_LONG_STARTED   → retracts spells with duration ≤ 8 hours
//     - CALENDAR_DAWN       → retracts spells with duration ≤ 24 hours
//   Manual dismiss via SpellsPanel is a separate path that calls
//   `dismissSpellEffect(sourceId)` directly.
// [Phase 4b CW-parity (C) follow-up — APR 2026]: Closes the timed-expiry gap
//   left in commit 44d6e41.

import effectRegistry from './effectRegistry';
import { effectCalculator } from './effectCalculator';
import { systemBus, EVENTS } from './systemBus';
import {
  expiresOnShortRest,
  expiresOnLongRest,
  expiresOnDayPass,
} from '../utils/spellDurationParser';

const SPELL_CAST_PREFIX = 'spell:cast:';

/**
 * Find every active spell-cast sourceId in effectRegistry whose duration
 * matches the given expiry predicate. Returns the sourceIds in registration
 * order so callers can retract them in a stable sequence (helpful for log
 * activity messages).
 *
 * @param {(durationMinutes: number) => boolean} predicate
 * @returns {string[]}
 */
function findExpiredSpellSources(predicate) {
  const expired = [];
  // metadata.entries() is the canonical iteration path — register() stores the
  // metadata block (incl. spellName + durationMinutes) keyed by sourceId.
  for (const [sourceId, meta] of effectRegistry.metadata.entries()) {
    if (!sourceId.startsWith(SPELL_CAST_PREFIX)) continue;
    const durationMinutes = meta?.durationMinutes;
    if (typeof durationMinutes !== 'number') continue; // null = open-ended, skip
    if (predicate(durationMinutes)) expired.push(sourceId);
  }
  return expired;
}

/**
 * Retract a list of source ids and clear the calculator cache. Returns the
 * count of sources actually retracted. Concentration spell sources keep their
 * own cleanup path — this service should never see them because castSpell
 * tags concentration spells with `concentration: true` metadata and the
 * predicate checks should skip those (enforced by the predicate caller).
 */
function retractSources(sourceIds) {
  if (!sourceIds || sourceIds.length === 0) return 0;
  for (const id of sourceIds) {
    try {
      effectRegistry.remove(id);
    } catch (err) {
      console.warn('[spellEffectExpiry] failed to remove source:', id, err);
    }
  }
  effectCalculator.clearCache();
  return sourceIds.length;
}

/**
 * [Phase 4b CW-parity (C) follow-up]: Public API — retracts all spell-cast
 *   sources whose duration matches the given expiry semantic. Exposed for
 *   tests and direct callers (e.g., a "force-expire all spell effects"
 *   developer button). Returns the number of sources retracted.
 */
export function expireSpellEffects(predicate, { logActivity, eventReason } = {}) {
  // Enforce the contract: never retract concentration spells via this path.
  // The metadata stores `concentration: true` for those; we filter them out.
  const safePredicate = (durationMinutes, meta) => {
    if (meta?.concentration) return false;
    return predicate(durationMinutes);
  };
  const expired = [];
  for (const [sourceId, meta] of effectRegistry.metadata.entries()) {
    if (!sourceId.startsWith(SPELL_CAST_PREFIX)) continue;
    const durationMinutes = meta?.durationMinutes;
    if (typeof durationMinutes !== 'number') continue;
    if (safePredicate(durationMinutes, meta)) {
      expired.push({ sourceId, spellName: meta?.spellName });
    }
  }
  const count = retractSources(expired.map(e => e.sourceId));
  if (count > 0 && logActivity) {
    const names = expired.map(e => e.spellName || '<unknown>').join(', ');
    logActivity(
      `${count} spell effect${count === 1 ? '' : 's'} expired (${names})${eventReason ? ` — ${eventReason}` : ''}`,
      'spell',
      { type: 'spell_effects_expired', sources: expired, eventReason }
    );
  }
  return count;
}

let _initialized = false;
let _unsubscribers = [];

/**
 * Initialize the spell-effect expiry listeners. Call once at app boot. Safe
 * to call multiple times — the second+ call is a no-op (returns the existing
 * teardown function).
 *
 * Returns a teardown function that unsubscribes the listeners. Mostly useful
 * for tests / hot-reload — production wiring runs for the lifetime of the app.
 *
 * @param {{ logActivity?: Function }} [options]
 * @returns {Function} teardown
 */
export function initSpellEffectExpiry({ logActivity } = {}) {
  if (_initialized) return teardownSpellEffectExpiry;

  const onShortRest = () => {
    expireSpellEffects(expiresOnShortRest, { logActivity, eventReason: 'short rest' });
  };
  const onLongRest = () => {
    expireSpellEffects(expiresOnLongRest, { logActivity, eventReason: 'long rest' });
  };
  const onDawn = () => {
    expireSpellEffects(expiresOnDayPass, { logActivity, eventReason: 'new day' });
  };

  _unsubscribers = [
    systemBus.on(EVENTS.REST_SHORT_STARTED, onShortRest),
    systemBus.on(EVENTS.REST_LONG_STARTED, onLongRest),
    systemBus.on(EVENTS.CALENDAR_DAWN, onDawn),
  ];
  _initialized = true;
  return teardownSpellEffectExpiry;
}

export function teardownSpellEffectExpiry() {
  for (const unsub of _unsubscribers) {
    if (typeof unsub === 'function') {
      try { unsub(); } catch (err) { /* swallow */ }
    }
  }
  _unsubscribers = [];
  _initialized = false;
}

/**
 * Test-only helper: returns whether the listener is currently initialized.
 * Useful for tests to assert single-init semantics.
 */
export function isSpellEffectExpiryInitialized() {
  return _initialized;
}
